import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle, Clock, Sparkles, BookOpen, Image } from 'lucide-react';

interface GenerationProgress {
  stage: 'story' | 'cover' | 'processing' | 'complete';
  percentage: number;
  message: string;
  substage?: string;
  estimatedTimeRemaining?: number;
}

interface ProgressTrackerProps {
  operation: 'story' | 'cover' | 'preview' | null;
  isVisible: boolean;
  characterName?: string;
  isMultiCharacter?: boolean;
}

export function GenerationProgressTracker({ 
  operation, 
  isVisible,
  characterName,
  isMultiCharacter 
}: ProgressTrackerProps) {
  const [progress, setProgress] = useState<GenerationProgress>({
    stage: 'story',
    percentage: 0,
    message: 'Initializing...',
    estimatedTimeRemaining: 45
  });
  
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Update progress based on operation and time
  useEffect(() => {
    if (!isVisible || !operation) return;
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);
      
      // Simulate progress based on typical timing
      if (operation === 'story') {
        if (elapsed < 5) {
          setProgress({
            stage: 'story',
            percentage: 10,
            message: 'Analyzing your story request...',
            substage: 'analysis',
            estimatedTimeRemaining: 40
          });
        } else if (elapsed < 15) {
          setProgress({
            stage: 'story',
            percentage: 30,
            message: `Creating ${characterName ? characterName + "'s" : 'your'} adventure...`,
            substage: 'story_generation',
            estimatedTimeRemaining: 30
          });
        } else if (elapsed < 25) {
          setProgress({
            stage: 'cover',
            percentage: 60,
            message: 'Designing the perfect cover scene...',
            substage: 'cover_design',
            estimatedTimeRemaining: 20
          });
        } else if (elapsed < 35) {
          setProgress({
            stage: 'cover',
            percentage: 80,
            message: 'Generating cover illustration...',
            substage: 'cover_image',
            estimatedTimeRemaining: 10
          });
        } else if (elapsed < 40) {
          setProgress({
            stage: 'processing',
            percentage: 95,
            message: 'Finalizing your story...',
            substage: 'finalization',
            estimatedTimeRemaining: 5
          });
        } else {
          setProgress({
            stage: 'complete',
            percentage: 100,
            message: 'Story ready!',
            substage: 'complete',
            estimatedTimeRemaining: 0
          });
        }
      } else if (operation === 'preview') {
        // Preview generation progress
        const totalTime = isMultiCharacter ? 90 : 60;
        const percentage = Math.min(95, (elapsed / totalTime) * 100);
        
        setProgress({
          stage: elapsed < totalTime * 0.5 ? 'story' : 'cover',
          percentage,
          message: elapsed < totalTime * 0.5 
            ? 'Generating book preview...' 
            : 'Creating cover and images...',
          estimatedTimeRemaining: Math.max(0, totalTime - elapsed)
        });
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [operation, isVisible, characterName, isMultiCharacter]);
  
  if (!isVisible) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="fixed bottom-4 right-4 bg-white rounded-lg shadow-xl p-4 w-80 z-50 border border-purple-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {progress.stage === 'complete' ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
            )}
            <span className="font-semibold text-gray-800">
              {operation === 'story' ? 'Creating Story' : 'Generating Preview'}
            </span>
          </div>
          {progress.estimatedTimeRemaining > 0 && (
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>{progress.estimatedTimeRemaining}s</span>
            </div>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-purple-600"
            animate={{ width: `${progress.percentage}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
        
        {/* Status message */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          {progress.stage === 'story' && <BookOpen className="w-4 h-4" />}
          {progress.stage === 'cover' && <Image className="w-4 h-4" />}
          {progress.stage === 'processing' && <Sparkles className="w-4 h-4" />}
          {progress.stage === 'complete' && <CheckCircle className="w-4 h-4 text-green-500" />}
          <span>{progress.message}</span>
        </div>
        
        {/* Stage indicators */}
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
          <StageIndicator 
            label="Story" 
            active={progress.stage === 'story'}
            complete={['cover', 'processing', 'complete'].includes(progress.stage)}
          />
          <StageIndicator 
            label="Cover" 
            active={progress.stage === 'cover'}
            complete={['processing', 'complete'].includes(progress.stage)}
          />
          <StageIndicator 
            label="Finalize" 
            active={progress.stage === 'processing'}
            complete={progress.stage === 'complete'}
          />
        </div>
        
        {/* Fun fact or tip */}
        {elapsedTime > 10 && elapsedTime < 35 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500"
          >
            💡 Tip: Each story is uniquely crafted for your child!
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function StageIndicator({ 
  label, 
  active, 
  complete 
}: { 
  label: string; 
  active: boolean; 
  complete: boolean; 
}) {
  return (
    <div className="flex-1">
      <div className={`
        text-xs text-center py-1 px-2 rounded
        ${complete ? 'bg-green-100 text-green-700' : ''}
        ${active ? 'bg-purple-100 text-purple-700 font-semibold' : ''}
        ${!complete && !active ? 'bg-gray-100 text-gray-400' : ''}
      `}>
        {label}
      </div>
    </div>
  );
}
